// Lightweight markdown renderer with DOMPurify sanitization (XSS-safe).
// Returns a SINGLE sanitized HTML string to avoid React reconciliation
// issues during streaming (insertBefore errors caused by browser extensions).
import DOMPurify from 'dompurify';

const escapeHtml = (s) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const renderInline = (s) => {
  let h = escapeHtml(s);
  h = h.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  h = h.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  h = h.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer noopener" data-mdlink="1">$1</a>');
  h = h.replace(/\n/g, '<br />');
  return h;
};

// Sanitizer config: allow only the tags we generate; strip anything else
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'code', 'pre', 'a', 'div', 'span', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'data-mdlink'],
  ALLOW_DATA_ATTR: false,
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/|#|\.\/)/i,
};

export const renderMarkdownHTML = (text) => {
  if (!text) return '';
  const parts = [];
  const codeRe = /```([a-zA-Z0-9_+-]*)\n?([\s\S]*?)```/g;
  let lastIdx = 0;
  let m;
  while ((m = codeRe.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push({ type: 'text', value: text.slice(lastIdx, m.index) });
    parts.push({ type: 'code', lang: m[1] || '', value: m[2] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push({ type: 'text', value: text.slice(lastIdx) });

  const rawHtml = parts.map((p) => {
    if (p.type === 'code') {
      const langTag = p.lang
        ? `<div class="code-lang">// ${escapeHtml(p.lang)}</div>`
        : '';
      return `<pre>${langTag}<code>${escapeHtml(p.value)}</code></pre>`;
    }
    return renderInline(p.value);
  }).join('');

  // Final sanitization step — strips any unsafe attributes or tags
  return DOMPurify.sanitize(rawHtml, SANITIZE_CONFIG);
};
